import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  catchError,
  dematerialize,
  delay,
  EMPTY,
  finalize,
  materialize,
} from 'rxjs';
import {
  LeafFieldNote,
  LeafLoadingAnimation,
} from '@cigna/leaf-web/lib/angular';
import { APP_CONTEXT, ENVIRONMENT_CONFIG, EnvironmentConfig } from './config';

@Directive({
  selector: 'a[cignaSecureCdnContent]',
})
export class CignaSecureCdnContentDirective {
  @Input() filePath?: string;
  @Input() forceTargetApp?: 'chcp' | 'evernorth';

  private isLoading = false;
  private spinnerRef?: ComponentRef<LeafLoadingAnimation>;
  private fieldNoteRef?: ComponentRef<LeafFieldNote>;

  constructor(
    private http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) private _config: EnvironmentConfig,
    @Inject(APP_CONTEXT) private appContext: string,
    private hostElement: ElementRef<HTMLAnchorElement>,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
  ) {}

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.filePath) {
      return;
    }

    event.preventDefault();

    if (this.isLoading) {
      return;
    }

    this.clearError();
    this.setLoadingState(true);

    const basePath =
      (this.appContext === 'evernorth' && this.forceTargetApp !== 'chcp') ||
      (this.appContext === 'chcp' && this.forceTargetApp === 'evernorth')
        ? `${this._config.redirect.resourceLibrary.replace(
            'static.cigna',
            'static.evernorth',
          )}secure/evernorth/provider`
        : `${this._config.redirect.resourceLibrary.replace(
            'static.evernorth',
            'static.cigna',
          )}secure/chcp/provider`;

    const url = `${basePath}${this.filePath}`;

    this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.showError(this.getErrorMessage(error));
          return EMPTY;
        }),
        finalize(() => {
          this.setLoadingState(false);
        }),
      )
      .subscribe((blob) => {
        const fileURL = window.URL.createObjectURL(blob);
        window.open(fileURL, '_blank', `noopener,noreferrer`);
      });
  }

  private setLoadingState(isLoading: boolean): void {
    this.isLoading = isLoading;

    if (isLoading) {
      this.renderer.setAttribute(
        this.hostElement.nativeElement,
        'aria-disabled',
        'true',
      );
      this.renderer.setStyle(
        this.hostElement.nativeElement,
        'pointer-events',
        'none',
      );
      this.renderer.setStyle(this.hostElement.nativeElement, 'opacity', '0.6');
      this.showSpinner();
      return;
    }

    this.renderer.removeAttribute(
      this.hostElement.nativeElement,
      'aria-disabled',
    );
    this.renderer.removeStyle(this.hostElement.nativeElement, 'pointer-events');
    this.renderer.removeStyle(this.hostElement.nativeElement, 'opacity');
    this.hideSpinner();
  }

  private showSpinner(): void {
    if (this.spinnerRef) {
      return;
    }

    this.spinnerRef =
      this.viewContainerRef.createComponent(LeafLoadingAnimation);
    this.renderer.setStyle(
      this.spinnerRef.location.nativeElement,
      'display',
      'inline-block',
    );
    this.renderer.setStyle(
      this.spinnerRef.location.nativeElement,
      'margin-left',
      '8px',
    );
  }

  private hideSpinner(): void {
    this.spinnerRef?.destroy();
    this.spinnerRef = undefined;
  }

  private showError(message: string): void {
    this.clearError();

    const content = this.renderer.createText(message);
    this.fieldNoteRef = this.viewContainerRef.createComponent(LeafFieldNote, {
      projectableNodes: [[content]],
    });
    this.fieldNoteRef.setInput('isError', true);
    this.renderer.setStyle(
      this.fieldNoteRef.location.nativeElement,
      'display',
      'block',
    );
    this.renderer.setStyle(
      this.fieldNoteRef.location.nativeElement,
      'margin-top',
      '8px',
    );
  }

  private clearError(): void {
    this.fieldNoteRef?.destroy();
    this.fieldNoteRef = undefined;
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (typeof error.error === 'string' && error.error.trim()) {
      return error.error;
    }

    if (error.error?.message) {
      return error.error.message;
    }

    if (error.message) {
      return error.message;
    }

    return 'Unable to load content. Please try again.';
  }
}
